import { supabase } from '../lib/supabase';

export const CONTEST_INTENT_KEY = 'contest_tier_intent';

export interface ContestTier {
  id: string;
  slug: string;
  name: string;
  description: string;
  price_cents: number;
  billing_mode: 'one_time' | 'subscription';
  period_label: string | null;
  participant_limit: number | null;
  accent_color: string;
  badge: string | null;
  highlighted: boolean;
  features: string[];
  duration_hours: number;
  sort_order: number;
}

export interface OwnedContest {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  logo_url: string | null;
  primary_color: string | null;
}

export const ContestService = {
  async getTiers(): Promise<ContestTier[]> {
    const { data, error } = await supabase
      .from('kiosk_contest_tiers')
      .select('id, slug, name, description, price_cents, billing_mode, period_label, participant_limit, accent_color, badge, highlighted, features, duration_hours, sort_order')
      .eq('is_active', true)
      .order('sort_order');
    if (error) throw new Error(error.message);
    return (data || []).map((t: any) => ({
      ...t,
      features: Array.isArray(t.features) ? t.features : [],
    }));
  },

  async hasActiveSubscription(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('singalong_purchases')
      .select('id, kiosk_contest_tiers!inner(billing_mode)')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .eq('kiosk_contest_tiers.billing_mode', 'subscription')
      .limit(1)
      .maybeSingle();

    if (error) return false;
    return !!data;
  },

  async getActiveSubscriptionPurchase() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('singalong_purchases')
      .select('id, user_id, pass_id, purchased_at, expires_at, status, league_id, contest_tier_id')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .not('contest_tier_id', 'is', null)
      .order('purchased_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;

    const { data: tier } = await supabase
      .from('kiosk_contest_tiers')
      .select('billing_mode')
      .eq('id', data.contest_tier_id)
      .maybeSingle();

    if (!tier || tier.billing_mode !== 'subscription') return null;
    return data;
  },

  async getUserContests(): Promise<OwnedContest[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('id, name, slug, status, created_at, logo_url, primary_color')
      .eq('owner_user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return data || [];
  },

  async createCheckout(tierId: string): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const baseUrl = window.location.origin;
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-contest-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          tier_id: tierId,
          success_url: `${baseUrl}/contests/setup?success=true`,
          cancel_url: `${baseUrl}/contests?canceled=true`,
        }),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Checkout failed');
    }

    const { url } = await res.json();
    if (!url) throw new Error('Checkout failed');
    return url;
  },

  async checkSlugAvailable(slug: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return !data;
  },

  async createContestLeague(params: {
    purchaseId?: string;
    name: string;
    slug: string;
  }): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: league, error: leagueError } = await supabase
      .from('kiosk_leagues')
      .insert({
        name: params.name,
        slug: params.slug,
        status: 'open',
        signup_mode: 'email_verified',
        owner_user_id: user.id,
        scoring_mode: 'best',
        allow_playback_default: true,
        primary_color: '#0ea5e9',
        secondary_color: '#0284c7',
        accent_color: '#f59e0b',
      })
      .select('id')
      .single();

    if (leagueError) throw new Error(leagueError.message);

    const { error: adminError } = await supabase
      .from('kiosk_league_admins')
      .insert({
        league_id: league.id,
        user_id: user.id,
        role: 'owner',
      });
    if (adminError) throw new Error(adminError.message);

    if (params.purchaseId) {
      const { error: purchaseError } = await supabase
        .from('singalong_purchases')
        .update({ league_id: league.id })
        .eq('id', params.purchaseId);
      if (purchaseError) throw new Error(purchaseError.message);
    }

    return league.id;
  },

  async duplicateContest(sourceId: string, params: { name: string; slug: string }): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: source, error: srcErr } = await supabase
      .from('kiosk_leagues')
      .select('*')
      .eq('id', sourceId)
      .eq('owner_user_id', user.id)
      .single();
    if (srcErr || !source) throw new Error('Source contest not found');

    const { data: league, error: leagueError } = await supabase
      .from('kiosk_leagues')
      .insert({
        name: params.name,
        slug: params.slug,
        status: 'open',
        signup_mode: source.signup_mode || 'email_verified',
        scoring_mode: source.scoring_mode || 'best',
        allow_playback_default: source.allow_playback_default ?? true,
        owner_user_id: user.id,
        description: source.description || '',
        primary_color: source.primary_color || '#0ea5e9',
        secondary_color: source.secondary_color || '#0284c7',
        accent_color: source.accent_color || '#f59e0b',
        logo_url: source.logo_url || null,
        hero_image_url: source.hero_image_url || null,
        background_image_url: source.background_image_url || null,
        signup_headline: source.signup_headline || '',
        signup_subtext: source.signup_subtext || '',
        sponsor_logos: source.sponsor_logos || [],
        sponsor_links: source.sponsor_links || [],
        leaderboard_top_n: source.leaderboard_top_n || null,
        rules: source.rules || null,
        rules_markdown: source.rules_markdown || null,
      })
      .select('id')
      .single();
    if (leagueError) throw new Error(leagueError.message);

    const { error: adminError } = await supabase
      .from('kiosk_league_admins')
      .insert({
        league_id: league.id,
        user_id: user.id,
        role: 'owner',
      });
    if (adminError) throw new Error(adminError.message);

    const { data: songs } = await supabase
      .from('kiosk_league_songs')
      .select('song_id, display_order')
      .eq('league_id', sourceId)
      .order('display_order');
    if (songs && songs.length > 0) {
      const rows = songs.map((s: any) => ({
        league_id: league.id,
        song_id: s.song_id,
        display_order: s.display_order,
      }));
      await supabase.from('kiosk_league_songs').insert(rows);
    }

    return league.id;
  },
};
